import React, { useCallback, useRef, useState } from 'react'
import { useApp } from '~/context/appContext'
import { redirectKioskLearner } from '~/service/PublicKiosk/fetchApiClient'
import KioskWelcomeBanner from '~/molecules/PublicKiosk/KioskWelcomeBanner'
import KioskLookupForm, { VALID_LOOKUP_MODES, requiredErrorFor } from '~/molecules/PublicKiosk/KioskLookupForm'
import KioskDocumentsPanel from '~/molecules/PublicKiosk/KioskDocumentsPanel'
import KioskConfirmDialog from '~/molecules/PublicKiosk/KioskConfirmDialog'
import AddNewLearnerKioskDialog from '~/molecules/PublicKiosk/AddNewLearnerKioskDialog'

const buildIdentifierParams = (lookupMode, textValue, selectedLearner) => {
  if (lookupMode === 'dropdown') return { learner_id: selectedLearner?.value }
  return { [lookupMode]: textValue }
}

const EMPTY_CONFIRM_STATE = { open: false, learnerName: '', params: null }

const restoreFocus = (element) => {
  if (!element) return
  // Wait a tick so Radix finishes unmounting the dialog before we steal focus back.
  window.requestAnimationFrame(() => {
    element.focus?.()
  })
}

const PublicKioskContent = () => {
  const { kioskData = {}, assets = {} } = useApp()
  const { kioskLink = {}, learners = [], policies = [], formMetadata = {} } = kioskData
  const { lookupMode: rawLookupMode, welcomeMessage, addLearner, secretId, trainingId } = kioskLink
  const lookupMode = VALID_LOOKUP_MODES.includes(rawLookupMode) ? rawLookupMode : 'dropdown'

  const [textValue, setTextValue] = useState('')
  const [selectedLearner, setSelectedLearner] = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const [confirmState, setConfirmState] = useState(EMPTY_CONFIRM_STATE)
  const [showAddLearnerDialog, setShowAddLearnerDialog] = useState(false)
  const addLearnerButtonRef = useRef(null)
  const lastDialogTriggerRef = useRef(null)

  const isDropdown = lookupMode === 'dropdown'

  const continueButtonEl = () => document.getElementById('kiosk-continue-button')

  const openAddLearnerDialog = useCallback(() => {
    lastDialogTriggerRef.current = addLearnerButtonRef.current
    setShowAddLearnerDialog(true)
  }, [])

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault()
    if (submittingRef.current) return

    if (isDropdown ? !selectedLearner : !textValue.trim()) {
      setError(requiredErrorFor(lookupMode))
      return
    }

    const params = buildIdentifierParams(lookupMode, textValue.trim(), selectedLearner)

    submittingRef.current = true
    setSubmitting(true)
    setError(null)
    // A new lookup attempt supersedes any identity still pending confirmation from a previous
    // attempt - without this, confirming after a second failed/changed lookup could complete
    // against the earlier match instead of the identifier just submitted.
    setConfirmState(EMPTY_CONFIRM_STATE)
    try {
      const data = await redirectKioskLearner(secretId, params)
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl
        return
      }
      if (data?.requiresConfirmation) {
        lastDialogTriggerRef.current = continueButtonEl()
        setConfirmState({ open: true, learnerName: data.learner?.name || '', params })
        return
      }
      setError('Something went wrong. Please try again.')
    } catch (err) {
      if (err.status === 404 && addLearner) {
        lastDialogTriggerRef.current = continueButtonEl()
        setShowAddLearnerDialog(true)
        return
      }
      setError(err.message)
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }, [isDropdown, selectedLearner, textValue, lookupMode, secretId, addLearner])

  const handleConfirm = useCallback(async () => {
    if (submittingRef.current || !confirmState.params) return

    submittingRef.current = true
    setSubmitting(true)
    try {
      const data = await redirectKioskLearner(secretId, { ...confirmState.params, redirect: true })
      if (data?.redirectUrl) {
        window.location.href = data.redirectUrl
        return
      }
      setConfirmState(EMPTY_CONFIRM_STATE)
      restoreFocus(lastDialogTriggerRef.current)
      setError('Something went wrong. Please try again.')
    } catch (err) {
      setConfirmState(EMPTY_CONFIRM_STATE)
      restoreFocus(lastDialogTriggerRef.current)
      setError(err.message)
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }, [confirmState.params, secretId])

  const handleCancelConfirm = useCallback((open) => {
    if (!open) {
      setConfirmState(EMPTY_CONFIRM_STATE)
      restoreFocus(lastDialogTriggerRef.current)
    }
  }, [])

  const handleAddLearnerOpenChange = useCallback((open) => {
    setShowAddLearnerDialog(open)
    if (!open) restoreFocus(lastDialogTriggerRef.current)
  }, [])

  return (
    <div className='flex flex-col items-center gap-8 max-w-4xl mx-auto py-6 sm:py-10 px-3 sm:px-4  lining-nums'>
      <KioskWelcomeBanner assets={assets} />

      <div className='flex flex-col md:flex-row gap-6 w-full items-stretch'>
        <KioskLookupForm
          lookupMode={lookupMode}
          welcomeMessage={welcomeMessage}
          addLearner={addLearner}
          learners={learners}
          textValue={textValue}
          onTextValueChange={setTextValue}
          selectedLearner={selectedLearner}
          onSelectedLearnerChange={setSelectedLearner}
          error={error}
          submitting={submitting}
          onSubmit={handleSubmit}
          onAddLearnerClick={openAddLearnerDialog}
          addLearnerButtonRef={addLearnerButtonRef}
        />

        <KioskDocumentsPanel policies={policies} assets={assets} />
      </div>

      <KioskConfirmDialog
        open={confirmState.open}
        learnerName={confirmState.learnerName}
        submitting={submitting}
        onOpenChange={handleCancelConfirm}
        onConfirm={handleConfirm}
      />

      {showAddLearnerDialog && (
        <AddNewLearnerKioskDialog
          open={showAddLearnerDialog}
          secretId={secretId}
          companyId={assets?.companyId}
          trainingId={trainingId}
          formMetadata={formMetadata}
          onOpenChange={handleAddLearnerOpenChange}
        />
      )}
    </div>
  )
}

export default PublicKioskContent
